/**
 * Pinned processing result for the Simple Flower practice design.
 *
 * Colour detection runs through Gemini, which returns its palette in no
 * particular order, and that order becomes the layer order in the Stitch
 * Editor. For ordinary uploads that's harmless, but the walkthrough says
 * "click layer 3 — the black outline", so a reshuffle sends the learner to the
 * wrong swatch. Tutorial designs therefore skip detection and use a fixed
 * palette instead.
 *
 * Values were sampled from the asset itself (see scripts/sampleTutorialColors.js):
 * pink 63.2% of pixels, black 33.5%, yellow 2.7%. The order below is also a
 * sensible stitch order — broad areas first, outline last on top.
 */
export const palette = [
  { hex: 'ff94c9', label: 'pink petals' },
  { hex: 'fdd835', label: 'yellow centre' },
  { hex: '000000', label: 'black outline' },
];

/** Shape `processAndAnalyzeImage` returns: `[hexWithoutHash, enabled]`. */
export const colors = palette.map(({ hex }) => [hex, true]);

/**
 * One-based layer numbers, matching the "Select layer N" controls.
 * The lesson's copy and its step targets are both built from these, so the two
 * can't drift apart if the palette order is ever changed.
 */
export const layers = {
  pink: 1,
  yellow: 2,
  outline: 3,
};

/**
 * Pinned stitch-settings analysis.
 *
 * Normally a model picks a starting stitch type per layer, which makes the
 * lesson different every time — and if it happened to choose satin for the
 * outline, the walkthrough's "Replace this layer" step would have nothing to
 * replace. Every layer therefore starts as a fill, which is what makes turning
 * the outline into satin an actual change the learner performs.
 *
 * `index` is zero-based and lines up with `palette`.
 */
export const aiAnalysis = {
  layers: [
    {
      index: 0,
      description: 'Five broad flower petals',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.4,
      max_stitch_length_mm: 2.8,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'Large rounded areas stitch cleanly as a standard fill.',
    },
    {
      index: 1,
      description: 'Small circular flower centre',
      recommended_stitch_type: 'auto_fill',
      angle: 90,
      row_spacing_mm: 0.4,
      max_stitch_length_mm: 2.6,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'A compact shape; cross-angle to the petals keeps the two distinct.',
    },
    {
      index: 2,
      description: 'Narrow black outline tracing the petals and centre',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.35,
      max_stitch_length_mm: 2.6,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'Starts as a fill — the walkthrough converts this band to satin.',
    },
  ],
  global: {
    suggested_density: 'normal',
    estimated_complexity: 'simple',
    warnings: [],
    tips: [
      'Stitch the petals and centre first, then the outline on top.',
      'Grow the colour areas slightly so no fabric shows along the outline.',
    ],
  },
  machine_recommendations: {
    stabilizer: {
      type: 'cut-away',
      weight: 'medium',
      reason: 'Broad filled areas pull the fabric and need stable backing.',
    },
    needle: {
      type: 'universal',
      size: '75/11',
      reason: 'Suits standard 40wt rayon or polyester thread.',
    },
    tension: { top: 'normal', bobbin: 'normal', notes: 'Standard settings are fine for this design.' },
    speed: 'medium',
    hooping: 'Hoop the fabric and stabiliser together, drum-tight.',
  },
};

const fixture = { palette, colors, layers, aiAnalysis };

export default fixture;
